import { evidenceSidebarOpenAtom } from '@/atoms/evidenceSidebarAtoms';
import { sessionPanelHostsAiSurfaceAtom } from '@heidi/session/data';

import { ASK_HEIDI_BAR_WIDTH, useAskHeidiBarWidth } from '@/atoms/sessionsBarAtoms';
import { useAskHeidiSurface } from '@/components/ui/session-ask-heidi/useAskHeidiSurface';
import { sessionListHost } from '@/composition/hosts/session/session-list/session-list-host';
import { useContainerWidth } from '@/hooks/useContainerWidth';
import { LAYOUT_PANEL_GROUP_CONTAINER_ID } from '@/hooks/useResizableSideBars';
import type { ImperativePanelHandle } from '@heidi/design-system/resizable';
import { useSessionsPanelLayout } from '@heidi/session';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const ASK_HEIDI_MIN_WIDTH_PX = 320;

// Keep in lockstep with `@heidi/session` MIN_PANEL_PX (280) + MIN_DETAIL_PX (480).
export const SESSION_EVIDENCE_THREE_COLUMN_MIN_PX = 280 + 480 + ASK_HEIDI_MIN_WIDTH_PX;

export const canFitSessionListWithEvidence = (containerWidth: number): boolean =>
  containerWidth >= SESSION_EVIDENCE_THREE_COLUMN_MIN_PX;

/**
 * How long a requested sessions-list collapse gets to flip `isSplit` (and so
 * re-run the open effect) before the width check runs anyway. The collapse is
 * a React state round-trip, well under this; the panel's 500ms slide is CSS
 * and does not delay `getSize()`, which reports the target layout.
 */
export const LIST_COLLAPSE_FALLBACK_MS = 300;

const applyOpenSize = (panel: ImperativePanelHandle, storedWidth: number) => {
  if (panel.isCollapsed() || panel.getSize() === 0) {
    panel.expand();
  }
  panel.resize(storedWidth);
};

/**
 * Manages the right-side evidence sidebar panel sizing, open/close,
 * and drag interactions. Simplified from useResizableSideBars since
 * it only handles one panel type (evidence/ask heidi).
 */
export const useEvidenceSidebarPanel = (
  panelContainerId = LAYOUT_PANEL_GROUP_CONTAINER_ID
) => {
  const evidenceSidebarOpen = useAtomValue(evidenceSidebarOpenAtom);
  // The V2 session-panel column can host the AI surface itself, in which case
  // this panel stands down so only one of the two paints the body. Stays
  // MOUNTED and merely collapsed rather than unrendered: it is a panel in a
  // `react-resizable-panels` group, and changing the group's panel count
  // mid-session would remount the group and drop the list/detail geometry.
  const columnHostsAiSurface = useAtomValue(sessionPanelHostsAiSurfaceAtom);
  // The panel is open only when the atom is true AND it is the surface elected
  // to paint Ask Heidi. Below md (or a sessions split that cannot fit) the
  // drawer takes over, so the resizable panel collapses via its useEffect —
  // letting both its closing animation and the drawer's opening animation run
  // simultaneously from the same breakpoint transition.
  const { surface } = useAskHeidiSurface();
  const isOpen = evidenceSidebarOpen && surface === 'panel' && !columnHostsAiSurface;
  const setEvidenceSidebarOpen = useSetAtom(evidenceSidebarOpenAtom);
  const { width: storedWidth, setWidth } = useAskHeidiBarWidth();
  const panelRef = useRef<ImperativePanelHandle>(null);
  const [isDragging, setIsDragging] = useState(false);
  const latestSizeRef = useRef<number>(0);
  const collapsedDuringDragRef = useRef(false);
  const containerWidth = useContainerWidth(panelContainerId);
  const { isSplit, panelActions } = useSessionsPanelLayout(sessionListHost);
  const collapseList = panelActions?.toDetail;

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || isDragging) return;

    if (!isOpen) {
      panel.collapse();
      return;
    }

    // Three columns cannot fit: ask the list to collapse. `isSplit` flips once
    // it does, re-running this effect against the freed width.
    const requestedListCollapse =
      isSplit &&
      containerWidth > 0 &&
      !canFitSessionListWithEvidence(containerWidth) &&
      collapseList !== undefined;
    if (requestedListCollapse) collapseList();

    applyOpenSize(panel, storedWidth);

    // Design-system `resize()` defers via queueMicrotask so v4 can sync min/max
    // after the open constraints commit. If we are still at 0 after that, hand
    // Ask Heidi back to the floating bar rather than leave the session with no
    // surface at all.
    //
    // A requested list collapse frees width asynchronously: `isSplit` flips,
    // this effect re-runs (the cleanup below cancels the pending check) and the
    // re-run measures against the freed width. Should the collapse never land —
    // `toDetail` a no-op, so nothing else re-runs this effect — the same check
    // runs on a bounded timer instead of leaving the open signal set behind a
    // 0-width panel.
    let cancelled = false;
    const handBackIfNoWidth = () => {
      if (cancelled) return;
      const next = panelRef.current;
      if (!next || next.getSize() > 0) return;
      setEvidenceSidebarOpen(false);
    };
    let collapseFallback: ReturnType<typeof setTimeout> | undefined;
    if (containerWidth > 0) {
      if (requestedListCollapse) {
        collapseFallback = setTimeout(handBackIfNoWidth, LIST_COLLAPSE_FALLBACK_MS);
      } else {
        queueMicrotask(handBackIfNoWidth);
      }
    }
    return () => {
      cancelled = true;
      if (collapseFallback !== undefined) clearTimeout(collapseFallback);
    };
  }, [
    isOpen,
    isDragging,
    storedWidth,
    containerWidth,
    isSplit,
    collapseList,
    setEvidenceSidebarOpen,
  ]);

  const handleResize = useCallback((size: number) => {
    if (size > 0) {
      latestSizeRef.current = size;
      collapsedDuringDragRef.current = false;
    } else {
      collapsedDuringDragRef.current = true;
    }
  }, []);

  const handleDragging = useCallback(
    (dragging: boolean) => {
      setIsDragging(dragging);
      if (!dragging) {
        if (collapsedDuringDragRef.current) {
          setEvidenceSidebarOpen(false);
          collapsedDuringDragRef.current = false;
        } else if (latestSizeRef.current > 0) {
          const constrained = Math.max(latestSizeRef.current, ASK_HEIDI_BAR_WIDTH.MIN);
          setWidth(constrained);
        }
        latestSizeRef.current = 0;
      }
    },
    [setWidth, setEvidenceSidebarOpen]
  );

  const panelConfig = useMemo(() => {
    const PERCENT_FLOOR = 20;
    let minSize: number = PERCENT_FLOOR;

    if (containerWidth > 0) {
      const dynamicMin = (ASK_HEIDI_MIN_WIDTH_PX / containerWidth) * 100;
      minSize = Math.min(Math.max(dynamicMin, PERCENT_FLOOR), ASK_HEIDI_BAR_WIDTH.MAX);
    }

    return {
      defaultSize: storedWidth,
      minSize,
      maxSize: ASK_HEIDI_BAR_WIDTH.MAX,
    };
  }, [storedWidth, containerWidth]);

  useEffect(() => {
    return () => {
      if (latestSizeRef.current > 0) {
        const constrained = Math.max(latestSizeRef.current, ASK_HEIDI_BAR_WIDTH.MIN);
        setWidth(constrained);
      }
    };
  }, [setWidth]);

  return {
    isOpen,
    panelRef,
    panelConfig,
    handleResize,
    isDragging,
    handleDragging,
  };
};
